import {
  deleteAssessment,
  getAssessment,
  patchAssessments
} from 'api/assessments';
import { Flex, Modal } from 'components';
import { IGenericModal } from 'components/Modal/Modal';
import { H2, P } from 'components/Typography/Typography';
import { SwitchButton } from 'components/_form';
import { IOption } from 'components/_form/Select/Select';
import { IOption as OptionForSelect } from 'components/_form/NestedSelect/NestedSelect';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CreateAssessmentBody } from 'types/assessments';
import { FormAssessment } from './FormAssessment/FormAssessment';
import { Loader } from 'components/Loader/Loader';
import { useAssessmentContext } from 'contexts/AssessmentContext';
import { iso8601HourAndHalf } from 'constants/iso8601';
import { onlyUniqueArray } from 'utilities/onlyUniqueArray';

export type FormData = Omit<
  CreateAssessmentBody,
  'assessment_participations_attributes'
>;

export type FormDataControl = FormData & {
  practice_id: IOption;
  version: IOption;
  assessment_type: IOption;
  assessment_participations_attributes: {
    value: string;
    label: string | JSX.Element;
    function_id?: string;
  }[];
};

interface Props extends IGenericModal {
  isOpen: boolean;
  onCancelClick: () => void;
}

export const EditAssessmentModal: React.FC<Props> = ({
  isOpen,
  onCancelClick
}) => {
  const [loading, setLoading] = useState(true);
  const [approvalSwitch, setApprovalSwitch] = useState(false);
  const [selectedPractice, setSelectedPractice] = useState<OptionForSelect[]>(
    []
  );

  const {
    handleLoadAssessments,
    assessmentToEditId,
    assessmentToEdit,
    setAssessmentToEdit
  } = useAssessmentContext();

  const { t } = useTranslation();

  const methods = useForm<FormDataControl>();

  const { handleSubmit, reset } = methods;

  const handleGetAssessmentToEdit = useCallback(
    async (id: number) => {
      setLoading(true);

      const assessmentRes = await getAssessment(id);

      if ('data' in assessmentRes && assessmentRes.data) {
        setSelectedPractice([
          {
            label: assessmentRes.data.practice.name,
            value: assessmentRes.data.practice.id.toString(10)
          }
        ]);

        setAssessmentToEdit(assessmentRes.data);
      }

      setLoading(false);
    },
    [setAssessmentToEdit]
  );

  const defaultFormValues: FormData = useMemo(() => {
    return {
      realization_date: new Date().toJSON().slice(0, 10).toString(),
      assessment_type: {
        value: assessmentToEdit?.assessment_type || 'normal',
        label: assessmentToEdit?.assessment_type || 'normal'
      },
      version: {
        value: '3.0-GW',
        label: '3.0-GW'
      },
      date: new Date().toJSON().slice(0, 10).toString(),
      name: assessmentToEdit?.name || '',
      description: assessmentToEdit?.description.body || '',
      practice_id:
        assessmentToEdit !== undefined ? assessmentToEdit.practice.id : 1,
      area_id: assessmentToEdit?.area.id.toString() || '',
      group: assessmentToEdit?.group || '',
      related_to_assessment_id:
        assessmentToEdit?.related_to_assessment?.id?.toString() || '',
      planned_duration:
        assessmentToEdit?.planned_duration_iso8601 || iso8601HourAndHalf,
      duration: assessmentToEdit?.duration_iso8601 || iso8601HourAndHalf,
      author_id: assessmentToEdit?.author?.id?.toString(),
      leader_id: assessmentToEdit?.leader?.id?.toString(),
      team_leader_id: assessmentToEdit?.team_leader?.id?.toString(),
      team_manager_id: assessmentToEdit?.team_manager?.id?.toString(),
      assessment_participations_attributes:
        assessmentToEdit?.assessment_participations?.map(
          (assessmentParticipation) => ({
            value: assessmentParticipation.participant.id,
            label: `${assessmentParticipation.participant.first_name} ${assessmentParticipation.participant.last_name}`,
            function_id: assessmentParticipation.function?.id
          })
        ),
      team_ids: assessmentToEdit?.teams?.map((team) => team.id.toString()),
      notes: assessmentToEdit?.notes || [],
      planned_start_date: assessmentToEdit?.planned_start_date,
      planned_completion_date: assessmentToEdit?.planned_completion_date,
      only_realization: assessmentToEdit?.only_realization,
      start_time: assessmentToEdit?.start_time,
      completion_time: assessmentToEdit?.completion_time,
      closing_ids: onlyUniqueArray(
        [
          ...(assessmentToEdit?.assessments_closings.map(({ user: { id } }) =>
            id.toString()
          ) || []),
          assessmentToEdit?.author?.id?.toString()
        ].filter((id) => id)
      )
    };
  }, [assessmentToEdit]);

  const handleSubmitAssessmentForm = useCallback(
    async (data: FormDataControl, closeOnSubmit = true) => {
      try {
        const actualAssessmentParticipationsAttributes = assessmentToEdit
          ? [
              // participants included in fetched list and not in selected list are removed
              ...(assessmentToEdit?.assessment_participations
                ?.filter(
                  (assessmentParticipation) =>
                    !data.assessment_participations_attributes?.find(
                      (assessmentParticipationAttribute) =>
                        assessmentParticipationAttribute.value.toString() ===
                        assessmentParticipation.participant?.id.toString()
                    )
                )
                .map((assessmentParticipation) => ({
                  id: assessmentParticipation.id,
                  _destroy: 1
                })) || []),
              // participants included in selected list and not in fetched list are removed
              ...(
                data.assessment_participations_attributes?.filter(
                  (assessmentParticipationAttribute) =>
                    !assessmentToEdit?.assessment_participations?.find(
                      (assessmentParticipation) =>
                        assessmentParticipationAttribute.value.toString() ===
                        assessmentParticipation.participant.id.toString()
                    )
                ) || []
              ).map((assessmentParticipationAttribute) => ({
                participant_id: assessmentParticipationAttribute.value,
                function_id: assessmentParticipationAttribute.function_id
              })),
              // participants included in both but with different function_id
              ...(
                assessmentToEdit?.assessment_participations?.filter(
                  (assessmentParticipation) =>
                    data.assessment_participations_attributes?.find(
                      (assessmentParticipationAttribute) =>
                        assessmentParticipationAttribute.value.toString() ===
                          assessmentParticipation.participant?.id.toString() &&
                        assessmentParticipationAttribute.function_id?.toString() !==
                          assessmentParticipation.function?.id.toString()
                    )
                ) || []
              ).map((assessmentParticipation) => ({
                id: assessmentParticipation.id,
                function_id: data.assessment_participations_attributes?.find(
                  (assessmentParticipationAttribute) =>
                    assessmentParticipationAttribute.value.toString() ===
                    assessmentParticipation.participant?.id.toString()
                )?.function_id
              }))
            ]
          : data.assessment_participations_attributes?.map(
              (assessmentParticipationAttribute) => ({
                participant_id: assessmentParticipationAttribute.value,
                function_id: assessmentParticipationAttribute.function_id
              })
            );

        const payload = {
          assessment: {
            ...data,
            type: 'Assessment',
            practice_id: Number(selectedPractice[0].value) || data.practice_id,
            assessment_type: data.assessment_type.value || data.assessment_type,
            author_id: JSON.parse(localStorage.getItem('currentUser') as string)
              .id,
            assessment_participations_attributes:
              actualAssessmentParticipationsAttributes
          }
        };

        const patchAssessmentsRes = await patchAssessments(
          assessmentToEdit?.id || 1,
          payload
        );
        await handleLoadAssessments();
        toast.success(t('common.success'));
        if (closeOnSubmit) {
          onCancelClick();
          reset(defaultFormValues);
        }

        if (patchAssessmentsRes.data) {
          return patchAssessmentsRes.data;
        }
      } catch (error) {
        console.log(error);
        toast.error(t('common.anErrorOccurred'));
      }
    },
    [
      handleLoadAssessments,
      onCancelClick,
      assessmentToEdit?.assessment_participations,
      assessmentToEdit?.id,
      assessmentToEdit,
      selectedPractice,
      t
    ]
  );

  const handleDeleteAssessment = async () => {
    if (assessmentToEdit) {
      const deleteAssessmentRes = await deleteAssessment(assessmentToEdit.id);

      if (deleteAssessmentRes && 'error' in deleteAssessmentRes) {
        return;
      }

      toast.success(t('common.success'));

      await handleLoadAssessments();
      onCancelClick();
    }
  };

  useEffect(() => {
    reset(defaultFormValues);
  }, [defaultFormValues, reset]);

  useEffect(() => {
    if (assessmentToEditId) {
      handleGetAssessmentToEdit(assessmentToEditId);
    }
  }, [assessmentToEditId, handleGetAssessmentToEdit]);

  return (
    <Modal
      isOpen={isOpen}
      onCancelClick={onCancelClick}
      mainButton={{
        action: () => {
          handleSubmit((data) => handleSubmitAssessmentForm(data))();
        },
        variant: 'eucalyptus',
        label: t(`common.save`)
      }}
      isSecondButtonVisible={true}
      gridTemplateColumns="95%"
      bodyMaxHeight="80vh"
      headerTitle={
        <Flex width="100%">
          <H2 variant="h3" mr={2}>
            {t(`assessmentsView.editAssessment`)}
          </H2>
        </Flex>
      }
      additionalFooter={
        <Flex
          minWidth="250px"
          alignItems="center"
          gap="10px"
          display={['none', 'flex']}
        >
          <P variant="body">{t('createTaskView.footer')}</P>
          <SwitchButton value={approvalSwitch} onChange={setApprovalSwitch} />
        </Flex>
      }
    >
      {loading ? (
        <Loader />
      ) : (
        <FormProvider {...methods}>
          <FormAssessment
            selectedPractice={selectedPractice}
            setSelectedPractice={setSelectedPractice}
            selectedAssessment={assessmentToEdit}
            setSelectedAssessment={setAssessmentToEdit}
            handleSubmitAssessmentForm={handleSubmitAssessmentForm}
            handleDeleteAssessment={handleDeleteAssessment}
          />
        </FormProvider>
      )}
    </Modal>
  );
};
